<template>
	<div class="cassie-vertical-md">
		<SectionCard>
			<template #title>
				{{ 'Page Behavior' | useLabels }}
			</template>
			<template #body>
				<p>
					Select where you would like the end user to be taken once they have submitted data.
				</p>
				<v-row dense>
					<v-col cols="6">
						<Dropdown
							label="Successful Outcome *"
							test-id="preference_page_outcome_success"
							:disabled="!userFullPermissions"
							:tooltip-text="successfulOutcomeTooltip"
							:value.sync="preferencePage.configurationOptions.successOutcome.confirmationPageTypeId"
							:items="successOutcomes"
							rules="required"
							@change="updateSuccessOutcomes('confirmationPageTypeId', $event)"
						/>
					</v-col>
				</v-row>
				<v-row
					v-if="shouldShowConfirmationDropdown"
					dense
				>
					<v-col cols="6">
						<Dropdown
							label="Select Successful Response Page *"
							test-id="preference_page_confirmation_page"
							searchable
							:disabled="!userFullPermissions"
							:value.sync="preferencePage.configurationOptions.successOutcome.internalConfirmationPageId"
							:items="filteredSuccessConfirmationPages"
							reserve-tooltip-space
							rules="required"
							@change="updateSuccessOutcomes('internalConfirmationPageId', $event)"
						/>
					</v-col>
				</v-row>
				<v-row
					v-if="shouldShowSuccessUrl"
					dense
				>
					<v-col cols="6">
						<TextField
							label="Select Destination URL *"
							test-id="preference_page_success_url"
							:disabled="!userFullPermissions"
							:value.sync="preferencePage.configurationOptions.successOutcome.externalConfirmationPageUrl"
							:rules="{required: true, isUrlwithHttp: true, max: 512}"
							reserve-tooltip-space
							@input="updateSuccessOutcomes('externalConfirmationPageUrl', $event)"
						/>
					</v-col>
				</v-row>
				<v-row
					v-if="shouldShowSuccessMessage"
					dense
				>
					<v-col cols="6">
						<TextField
							label="Preference Updated Success Message"
							test-id="preference_page_success_message"
							:disabled="!userFullPermissions"
							:value.sync="preferencePage.configurationOptions.successOutcome.preferenceUpdateSuccessMessage"
							:rules="{max: 500}"
							reserve-tooltip-space
							@input="updateSuccessOutcomes('preferenceUpdateSuccessMessage', $event)"
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<Dropdown
							v-model="failureOutcomeType"
							label="Failure Outcome *"
							test-id="preference_page_outcome_failure"
							:disabled="!userFullPermissions"
							:tooltip-text="failureOutcomeTooltip"
							:items="failureOutcomes"
							rules="required"
							@change="updateFailureOutcomeType($event)"
						/>
					</v-col>
				</v-row>
				<v-row
					v-if="shouldShowFailurePageDropdown"
					dense
				>
					<v-col cols="6">
						<Dropdown
							label="Select Failure Response Page *"
							test-id="preference_page_failure_confirmation_page"
							searchable
							:disabled="!userFullPermissions"
							:value="failureResponsePageId"
							:items="filteredFailureConfirmationPages"
							reserve-tooltip-space
							@change="updateFailureResponsePage($event)"
						/>
					</v-col>
				</v-row>
				<v-row
					v-if="shouldShowFailureUrl"
					dense
				>
					<v-col cols="6">
						<TextField
							label="Failure Destination URL *"
							test-id="preference_page_failure_url"
							:disabled="!userFullPermissions"
							:value.sync="preferencePage.customErrorUrl"
							reserve-tooltip-space
							:rules="{required: true, isUrlwithHttp: true, max: 512}"
							@input="updatePreferencePage('customErrorUrl', $event)"
						/>
					</v-col>
				</v-row>
				<v-row
					v-if="shouldShowFailureMessage"
					dense
				>
					<v-col cols="6">
						<TextField
							label="Preference Update Failed Message"
							test-id="preference_page_failure_message"
							:disabled="!userFullPermissions"
							:value.sync="preferencePage.configurationOptions.successOutcome.preferenceUpdateFailedMessage"
							:rules="{max: 500}"
							reserve-tooltip-space
							@input="updateSuccessOutcomes('preferenceUpdateFailedMessage', $event)"
						/>
					</v-col>
				</v-row>
			</template>
		</SectionCard>
		<ConsentCollectionPrivacyPolicyCard
			:assigned-languages="assignedLanguages"
			:user-full-permissions="userFullPermissions"
			:custom-privacy-policies.sync="preferencePage.customPrivacyPolicies"
			:brand-id.sync="preferencePage.brandId"
			type="preference_page"
			@update-privacy-policies="updatePrivacyPolicies($event)"
			@update-privacy-policies-enabled="updatePrivacyPoliciesEnabled($event)"
		/>
	</div>
</template>

<script>
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import ConsentCollectionPrivacyPolicyCard from '../../consent-collection-privacy-policy-card.vue'

const outcomes = {
	URL_OUTCOME: 1,
	PAGE_OUTCOME: 2,
	REMAIN_ON_PAGE_OUTCOME: 3, // TBC
	FAILURE_URL: 'Forward To URL'
}

export default {
	components: {
		SectionCard,
		Dropdown,
		TextField,
		ConsentCollectionPrivacyPolicyCard
	},
	props: {
		preferencePage: Object,
		userFullPermissions: Boolean,
		confirmationPages: Array,
		assignedLanguages: Array
	},
	data () {
		return {
			failureOutcomeType: null
		}
	},
	computed: {
		filteredSuccessConfirmationPages () {
			return this.baseFilteredConfirmationPages.filter(p => p.type === 1 || p.type == null).map(p => ({ text: p.name, value: p.id }))
		},
		filteredFailureConfirmationPages () {
			const defaultOption = { text: '-- Forward to Default Failure Page', value: null }
			const items = this.baseFilteredConfirmationPages.filter(p => p.type === 2 || p.type == null).map(p => ({ text: p.name, value: p.id }))
			return [defaultOption, ...items]
		},
		baseFilteredConfirmationPages () {
			const UNIVERSAL_BRAND = 0
			if (!this.confirmationPages) return []
			return this.confirmationPages
				.filter(({ brandId }) => brandId === 0 || brandId === this.preferencePage.brandId || brandId === UNIVERSAL_BRAND)
				.map(({ confirmationPageId, confirmationPageName, confirmationPageTypeId, confirmationPageType }) => ({
					id: confirmationPageId,
					name: confirmationPageName,
					type: confirmationPageTypeId ?? confirmationPageType
				}))
		},
		failureResponsePageId () {
			return this.preferencePage.configurationOptions?.successOutcome?.failureResponsePageId ?? null
		},
		confirmationPageTypeId () {
			return this.preferencePage.configurationOptions.successOutcome.confirmationPageTypeId
		},
		successOutcomes () {
			return [
				{ text: 'Forward to Successful Response Page', value: outcomes.PAGE_OUTCOME },
				{ text: 'Forward to URL', value: outcomes.URL_OUTCOME },
				{ text: 'Remain on Page', value: outcomes.REMAIN_ON_PAGE_OUTCOME }
			]
		},
		failureOutcomes () {
			return [
				{ text: 'Forward to URL', value: outcomes.URL_OUTCOME },
				{ text: 'Forward to Failure Response Page', value: outcomes.PAGE_OUTCOME },
				{ text: 'Remain on Page', value: outcomes.REMAIN_ON_PAGE_OUTCOME }
			]
		},
		successfulOutcomeTooltip () {
			switch (this.confirmationPageTypeId) {
				case outcomes.PAGE_OUTCOME:
					return 'Following a successful Preference update, the end user will be redirected to the chosen Response Page'
				case outcomes.URL_OUTCOME:
					return 'Following a successful Preference update, the end user will be redirected to the Destination URL specified.  Note this must begin with http:// or https://'
				case outcomes.REMAIN_ON_PAGE_OUTCOME:
					return `Following a successful Preference update the end user will remain on the page and receive an optional Preference Updated 'success' message confirming the submission has been received.`
				default:
					return 'Select the type of response if the Link update is successful'
			}
		},
		failureOutcomeTooltip () {
			switch (this.failureOutcomeType) {
				case outcomes.PAGE_OUTCOME:
					return 'If the update is unsuccessful, the end user will be redirected to the chosen Failure Response Page'
				case outcomes.URL_OUTCOME:
					return 'If the update is unsuccessful, the end user will be redirected to the Destination URL specified.  Note this must begin with http:// or https://'
				case outcomes.REMAIN_ON_PAGE_OUTCOME:
					return 'If the update is unsuccessful, the end user will remain on the page and receiving a plain text failure message.'
				default:
					return 'Select the type of response if the Link update fails'
			}
		},
		shouldShowConfirmationDropdown () {
			return this.confirmationPageTypeId === outcomes.PAGE_OUTCOME
		},
		shouldShowFailurePageDropdown () {
			return this.failureOutcomeType === outcomes.PAGE_OUTCOME
		},
		shouldShowFailureUrl () {
			return this.failureOutcomeType === outcomes.URL_OUTCOME
		},
		shouldShowSuccessUrl () {
			return this.confirmationPageTypeId === outcomes.URL_OUTCOME
		},
		shouldShowSuccessMessage () {
			return this.confirmationPageTypeId === outcomes.REMAIN_ON_PAGE_OUTCOME
		},
		shouldShowFailureMessage () {
			return this.failureOutcomeType === outcomes.REMAIN_ON_PAGE_OUTCOME
		}
	},
	created () {
		const failureTypeId = this.preferencePage.configurationOptions?.successOutcome?.failureTypeId
		const customErrorUrl = this.preferencePage.customErrorUrl
		const failureMessage = this.preferencePage.configurationOptions?.successOutcome?.preferenceUpdateFailedMessage
		const failurePageId = this.preferencePage.configurationOptions?.successOutcome?.failureResponsePageId ?? null

		let determinedValue
		if (failureTypeId !== null && failureTypeId !== undefined) {
			determinedValue = failureTypeId
		} else if (customErrorUrl !== null && customErrorUrl !== '') {
			determinedValue = outcomes.URL_OUTCOME
		} else if (failureMessage !== null && failureMessage !== undefined && failureMessage !== '') {
			determinedValue = outcomes.REMAIN_ON_PAGE_OUTCOME
		} else if (failurePageId !== null) {
			determinedValue = outcomes.PAGE_OUTCOME
		} else {
			determinedValue = outcomes.PAGE_OUTCOME
		}

		this.failureOutcomeType = determinedValue
		if ((failureTypeId === null || failureTypeId === undefined) && determinedValue !== null) {
			this.$nextTick(() => {
				this.$emit('update:preferencePage', {
					...this.preferencePage,
					configurationOptions: {
						...this.preferencePage.configurationOptions,
						successOutcome: {
							...this.preferencePage.configurationOptions.successOutcome,
							failureTypeId: determinedValue
						}
					}
				})
			})
		}
	},
	methods: {
		updatePreferencePage (property, value) {
			this.$emit('update:preferencePage', {
				...this.preferencePage,
				[property]: value
			})
		},
		updateSuccessOutcomes (property, value) {
			if (property === 'confirmationPageTypeId') {
				const updatedSuccessOutcome = {
					confirmationPageTypeId: value,
					internalConfirmationPageId: null,
					externalConfirmationPageUrl: null,
					preferenceUpdateSuccessMessage: null,
					failureResponsePageId: this.preferencePage.configurationOptions.successOutcome.failureResponsePageId,
					failureTypeId: this.preferencePage.configurationOptions.successOutcome.failureTypeId,
					preferenceUpdateFailedMessage: this.preferencePage.configurationOptions.successOutcome.preferenceUpdateFailedMessage
				}

				this.$emit('update:preferencePage', {
					...this.preferencePage,
					configurationOptions: {
						...this.preferencePage.configurationOptions,
						successOutcome: updatedSuccessOutcome
					}
				})
			} else {
				this.$emit('update:preferencePage', {
					...this.preferencePage,
					configurationOptions: {
						...this.preferencePage.configurationOptions,
						successOutcome: {
							...this.preferencePage.configurationOptions.successOutcome,
							[property]: value
						}
					}
				})
			}
		},
		updateFailureOutcomeType (value) {
			this.failureOutcomeType = value
			this.updateSuccessOutcomes('failureTypeId', value)

			this.$nextTick(() => {
				if (value === outcomes.URL_OUTCOME) {
					this.updateFailureResponsePage(null)
					this.updateSuccessOutcomes('preferenceUpdateFailedMessage', null)
				} else if (value === outcomes.PAGE_OUTCOME) {
					this.updatePreferencePage('customErrorUrl', null)
					this.updateSuccessOutcomes('preferenceUpdateFailedMessage', null)
				} else if (value === outcomes.REMAIN_ON_PAGE_OUTCOME) {
					this.updateFailureResponsePage(null)
					this.updatePreferencePage('customErrorUrl', null)
				}
			})
		},
		updateFailureResponsePage (value) {
			this.$emit('update:preferencePage', {
				...this.preferencePage,
				configurationOptions: {
					...this.preferencePage.configurationOptions,
					successOutcome: {
						...this.preferencePage.configurationOptions.successOutcome,
						failureResponsePageId: value
					}
				}
			})
		},
		updatePrivacyPolicies (privacyPolicies) {
			this.updatePreferencePage('customPrivacyPolicies', privacyPolicies)
		},
		updatePrivacyPoliciesEnabled (isEnabled) {
			this.updatePreferencePage('customPrivacyPoliciesEnabled', isEnabled)
		}
	}
}
</script>
